<template>
  <v-dialog persistent
            v-bind:value="value"
            v-on:input="$emit('input', $event)"
            width="640">
    <v-card>
      <v-card-title>
        {{ $t('title') }}
      </v-card-title>
      <v-card-text>
        <p class="sf-muted mb-4">
          <template v-if="recipientIds != null">{{ $tc('selectedRecipients', recipientIds.length, { label: recipientLabel }) }}</template>
          <template v-else>{{ audience === 'Supplier' ? $t('allSuppliers') : $t('allCustomers') }}</template>
        </p>
        <v-select dense
                  hide-details="auto"
                  item-text="name"
                  item-value="id"
                  outlined
                  v-bind:items="templates"
                  v-bind:label="$t('template')"
                  v-model="templateId"
                  v-on:change="refreshPreview" />
        <v-switch class="mt-4"
                  color="primary"
                  hide-details
                  inset
                  v-bind:label="$t('includeGuests')"
                  v-if="audience !== 'Supplier'"
                  v-model="includeGuests"
                  v-on:change="refreshPreview" />
        <v-switch class="mt-2"
                  color="primary"
                  hide-details
                  inset
                  v-bind:label="$t('skipAlreadySent')"
                  v-model="skipAlreadySent"
                  v-on:change="refreshPreview" />

        <v-progress-linear class="mt-6"
                           color="accent"
                           indeterminate
                           v-if="loading" />
        <v-simple-table class="mt-6 sf-kv"
                        dense
                        v-else-if="preview != null">
          <tbody>
            <tr>
              <td>{{ $t('recipients') }}</td>
              <td class="text-right"><strong>{{ preview.recipients }}</strong></td>
            </tr>
            <tr v-if="audience !== 'Supplier'">
              <td>{{ $t('guests') }}</td>
              <td class="text-right"><strong>{{ preview.guests }}</strong></td>
            </tr>
            <tr v-if="preview.skippedByState > 0">
              <td>{{ $t('skippedByState', { state: stateLabel(preview.onlyIfState) }) }}</td>
              <td class="text-right">{{ preview.skippedByState }}</td>
            </tr>
            <tr v-if="preview.alreadySent > 0">
              <td>{{ $t('alreadySent') }}</td>
              <td class="text-right">{{ preview.alreadySent }}</td>
            </tr>
            <tr v-if="preview.invalidEmail > 0">
              <td>{{ $t('invalidEmail') }}</td>
              <td class="text-right">{{ preview.invalidEmail }}</td>
            </tr>
            <tr v-if="preview.duplicates > 0">
              <td>{{ $t('duplicates') }}</td>
              <td class="text-right">{{ preview.duplicates }}</td>
            </tr>
            <tr v-if="preview.changeToState">
              <td colspan="2" class="text-caption">{{ $t('changeToState', { state: preview.changeToState }) }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-alert class="mt-4 mb-0"
                 dense
                 text
                 type="error"
                 v-if="error">
          {{ error }}
        </v-alert>
        <v-alert class="mt-4 mb-0"
                 dense
                 text
                 type="warning"
                 v-if="emailSettings != null && emailSettings.testMode">
          {{ emailSettings.guardMode }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text
               v-on:click="close">
          {{ $t('cancel') }}
        </v-btn>
        <v-btn color="accent"
               depressed
               v-bind:disabled="preview == null || preview.total === 0 || loading"
               v-bind:loading="sending"
               v-on:click="send">
          <v-icon left>ti-mail-fast</v-icon>
          {{ $tc('sendTo', preview != null ? preview.total : 0, { count: preview != null ? preview.total : 0 }) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  export default {
    computed: {
      emailSettings() {
        return this.$store.getters.emailDesigner.settings
      },
      templates() {
        // Altformat-Vorlagen sind nicht versendbar, bis sie umgewandelt wurden.
        return this.$store.getters.customEmailTemplates.filter(t => t.audience === this.audience && t.kind !== 'Layout' && !t.requiresConversion)
      }
    },
    data: () => ({
      error: null,
      includeGuests: true,
      loading: false,
      preview: null,
      sending: false,
      skipAlreadySent: true,
      templateId: null
    }),
    methods: {
      /** Deutscher Name des Status für die Vorschau (open/invited/accepted/declined). */
      stateLabel(state) {
        const key = 'state' + (state || '').charAt(0).toUpperCase() + (state || '').slice(1)

        return this.$te(key) ? this.$t(key) : state
      },
      close() {
        this.$emit('input', false)
      },
      request() {
        return {
          templateId: this.templateId,
          recipientIds: this.recipientIds,
          includeGuests: this.includeGuests,
          skipAlreadySent: this.skipAlreadySent
        }
      },
      async refreshPreview() {
        this.error = null
        this.preview = null

        if (this.templateId == null) {
          return
        }

        this.loading = true

        try {
          this.preview = await this.$store.dispatch('previewEmailJob', this.request())
        } catch (e) {
          this.error = e.message
        } finally {
          this.loading = false
        }
      },
      async send() {
        this.sending = true
        this.error = null

        try {
          const job = await this.$store.dispatch('createEmailJob', this.request())

          this.$emit('created', job)
          this.close()
        } catch (e) {
          this.error = e.message
        } finally {
          this.sending = false
        }
      }
    },
    name: 'SendEmailDialog',
    props: {
      audience: {
        default: 'Customer',
        type: String
      },
      recipientIds: Array,
      recipientLabel: String,
      value: Boolean
    },
    watch: {
      value(open) {
        if (open) {
          this.$store.dispatch('loadEmailDesignerData')
          this.preview = null
          this.error = null

          if (this.templateId == null || !this.templates.some(t => t.id === this.templateId)) {
            this.templateId = this.templates.length ? this.templates[0].id : null
          }

          this.refreshPreview()
        }
      }
    }
  }
</script>

<i18n>
  {
    "de": {
      "title": "E-Mail senden",
      "selectedRecipients": "Empfänger: {label} | Empfänger: {label} | {count} ausgewählte Empfänger",
      "allCustomers": "Empfänger: alle Kunden (nach Status-Filter der Vorlage)",
      "allSuppliers": "Empfänger: alle Lieferanten (nach Status-Filter der Vorlage)",
      "template": "Vorlage",
      "includeGuests": "Auch an hinterlegte Gäste senden (jeder Gast erhält seinen eigenen Einladungscode, auch bei gleicher E-Mail-Adresse)",
      "skipAlreadySent": "Empfänger überspringen, die diese Vorlage bereits erhalten haben",
      "recipients": "Empfänger",
      "guests": "Gäste",
      "skippedByState": "Übersprungen (Status ist nicht „{state}“)",
      "alreadySent": "Übersprungen (bereits erhalten)",
      "invalidEmail": "Ohne gültige E-Mail-Adresse",
      "duplicates": "Doppelte Personen (dieselbe Person mehrfach im Auftrag)",
      "stateOpen": "Offen",
      "stateInvited": "Eingeladen",
      "stateAccepted": "Angenommen",
      "stateDeclined": "Abgelehnt",
      "changeToState": "Nach erfolgreichem Versand wird der Status auf „{state}“ gesetzt.",
      "cancel": "Abbrechen",
      "sendTo": "Keine Empfänger | An 1 Empfänger senden | An {count} Empfänger senden"
    },
    "en": {
      "title": "Send email",
      "selectedRecipients": "Recipient: {label} | Recipient: {label} | {count} selected recipients",
      "allCustomers": "Recipients: all customers (filtered by the template's state)",
      "allSuppliers": "Recipients: all suppliers (filtered by the template's state)",
      "template": "Template",
      "includeGuests": "Also send to registered guests (each guest receives their own invitation code, even with the same email address)",
      "skipAlreadySent": "Skip recipients who already received this template",
      "recipients": "Recipients",
      "guests": "Guests",
      "skippedByState": "Skipped (state is not “{state}”)",
      "alreadySent": "Skipped (already received)",
      "invalidEmail": "Without valid email address",
      "duplicates": "Duplicate persons (the same person more than once in this job)",
      "stateOpen": "Open",
      "stateInvited": "Invited",
      "stateAccepted": "Accepted",
      "stateDeclined": "Declined",
      "changeToState": "After successful delivery the state is set to “{state}”.",
      "cancel": "Cancel",
      "sendTo": "No recipients | Send to 1 recipient | Send to {count} recipients"
    }
  }
</i18n>
